import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ShieldCheck, Sparkles, Lock, Landmark, Copy, Info, Phone } from 'lucide-react';
import * as Icons from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { applianceTypes, applianceBrands, APPLIANCE_PLAN_PRICING, ALL_INCLUSIVE_PLAN, BOILER_PLANS } from '../mock';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { usePlatform } from '../contexts/PlatformContext';
import { COMPANY } from '../constants/company';
import { validateEmail } from '../utils/validators';

const PLAN_TYPES = [
  { id: 'appliance', name: 'Appliance Service Plan', tag: 'Cover 1 to 5 appliances', maxAppliances: 5, allowsMonthly: true },
  { id: 'all-inclusive', name: 'All Inclusive Home Protection', tag: 'Up to 7 appliances', maxAppliances: ALL_INCLUSIVE_PLAN.maxAppliances, allowsMonthly: true },
  { id: 'boiler-only', name: 'Boiler Only', tag: 'Boiler maintenance & repair support', maxAppliances: 0, allowsMonthly: false, annualPrice: BOILER_PLANS[0].annual },
  { id: 'boiler-plus-drainage', name: 'Boiler + Plumbing & Drainage', tag: 'Boiler + plumbing & drainage support', maxAppliances: 0, allowsMonthly: false, annualPrice: BOILER_PLANS[1].annual },
];

const steps = ['Plan', 'Appliances', 'Billing', 'Your details', 'Payment'];

const StepIndicator = ({ current, hideAppliances }) => {
  const visible = steps.filter((_, i) => !(hideAppliances && i === 1));
  return (
    <div className="flex items-center justify-between max-w-3xl mx-auto mb-10">
      {visible.map((s, i) => {
        // Map visible index back to original step index for progress colouring.
        const originalIdx = hideAppliances && i >= 1 ? i + 1 : i;
        const isDone = originalIdx < current;
        const isNow = originalIdx === current;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${isDone ? 'bg-[#00b67a] text-white' : isNow ? 'bg-[#1F0068] text-white' : 'bg-[#1F0068]/10 text-[#1F0068]/50'}`}>
                {isDone ? <Check className="w-5 h-5" /> : i + 1}
              </div>
              <div className={`text-[11px] font-semibold uppercase tracking-wider hidden sm:block ${isNow ? 'text-[#1F0068]' : 'text-[#1F0068]/50'}`}>{s}</div>
            </div>
            {i < visible.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 sm:mx-2 ${isDone ? 'bg-[#00b67a]' : 'bg-[#1F0068]/10'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const priceForApplianceCount = (n) => APPLIANCE_PLAN_PRICING.find((p) => p.count === n);

const GetQuote = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    planType: '',
    applianceIds: [],
    brand: '',
    model: '',
    billing: 'annual', // 'monthly' or 'annual'
    name: '',
    email: '',
    phone: '',
    postcode: '',
    address: '',
    marketing: false,
    paymentAcknowledged: false,
  });
  const [paymentRef, setPaymentRef] = useState('');
  const nav = useNavigate();
  const { toast } = useToast();
  const { user, register } = useAuth();
  const { previewNextPlanNumber } = usePlatform();

  const planType = PLAN_TYPES.find((p) => p.id === data.planType);
  const hideAppliancesStep = planType && planType.maxAppliances === 0;

  const selectedAppliances = applianceTypes.filter((a) => data.applianceIds.includes(a.id));

  // Reserve plan-number reference the moment the customer lands on the payment step.
  useEffect(() => {
    if (step === 4 && !paymentRef && previewNextPlanNumber) {
      setPaymentRef(previewNextPlanNumber());
    }
  }, [step, paymentRef, previewNextPlanNumber]);

  const upd = (patch) => setData((d) => ({ ...d, ...patch }));
  const toggleAppliance = (id) => setData((d) => {
    const has = d.applianceIds.includes(id);
    if (has) return { ...d, applianceIds: d.applianceIds.filter((x) => x !== id) };
    const cap = planType?.maxAppliances || 0;
    if (cap && d.applianceIds.length >= cap) {
      toast({ title: `This plan covers up to ${cap} appliances`, description: 'Please remove one before adding another.' });
      return d;
    }
    return { ...d, applianceIds: [...d.applianceIds, id] };
  });

  // Compute pricing based on plan type + appliance count + billing frequency.
  const pricing = useMemo(() => {
    if (!planType) return { monthly: 0, annual: 0, label: '', canMonthly: false };
    if (planType.id === 'appliance') {
      const count = Math.min(data.applianceIds.length, 5);
      const row = priceForApplianceCount(count) || { monthly: 0, annual: 0, name: '—' };
      return { monthly: row.monthly, annual: row.annual, label: row.name, canMonthly: true };
    }
    if (planType.id === 'all-inclusive') {
      return { monthly: ALL_INCLUSIVE_PLAN.monthly, annual: ALL_INCLUSIVE_PLAN.annual, label: ALL_INCLUSIVE_PLAN.name, canMonthly: true };
    }
    // Boiler plans — annual only.
    return { monthly: null, annual: planType.annualPrice, label: planType.name, canMonthly: false };
  }, [planType, data.applianceIds.length]);

  const currentPrice = data.billing === 'monthly' && pricing.canMonthly ? pricing.monthly : pricing.annual;
  const currentBillingLabel = data.billing === 'monthly' && pricing.canMonthly ? 'per month' : 'per year';

  const next = () => {
    if (step === 0 && !data.planType) return toast({ title: 'Please choose a plan type' });
    if (step === 1 && !hideAppliancesStep) {
      if (data.applianceIds.length === 0) return toast({ title: 'Pick at least one appliance' });
      if (!data.brand) return toast({ title: 'Please choose the appliance brand' });
    }
    if (step === 3) {
      if (!data.name || !data.email || !data.postcode) return toast({ title: 'Please complete your details' });
      const em = validateEmail(data.email);
      if (!em.ok) return toast({ title: 'Email needs a small tweak', description: em.error });
      if (em.formatted !== data.email) upd({ email: em.formatted });
    }
    // Skip step 1 (Appliances) for boiler plans
    let nextStep = step + 1;
    if (nextStep === 1 && hideAppliancesStep) nextStep = 2;
    setStep(Math.min(nextStep, steps.length - 1));
  };

  const back = () => {
    let prev = step - 1;
    if (prev === 1 && hideAppliancesStep) prev = 0;
    setStep(Math.max(0, prev));
  };

  const complete = () => {
    if (!data.paymentAcknowledged) {
      return toast({ title: 'Please tick the confirmation', description: 'Confirm you have noted the bank transfer details so you can pay from your online banking.' });
    }
    if (!user) {
      const pw = 'temp' + Math.floor(Math.random() * 1000);
      register({ name: data.name, email: data.email, password: pw });
      toast({
        title: `Plan ${paymentRef} received`,
        description: `Please pay £${Number(currentPrice).toFixed(2)} to sort code ${COMPANY.bank.sortCode} · account ${COMPANY.bank.accountNumber} using reference ${paymentRef}. Your temporary password: ${pw}.`,
      });
    } else {
      toast({
        title: `Plan ${paymentRef} added to your account`,
        description: `Please pay £${Number(currentPrice).toFixed(2)} using reference ${paymentRef}. You can view your invoice on your dashboard.`,
      });
    }
    setTimeout(() => nav('/dashboard'), 1500);
  };

  const copyToClipboard = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied`, description: value });
    } catch {
      toast({ title: 'Copy failed', description: 'Please select and copy manually.' });
    }
  };

  return (
    <div className="bg-dhs min-h-screen text-primary-dhs">
      <Header />
      <main className="py-10 md:py-16">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white text-[#1F0068] rounded-full px-3 py-1 text-xs font-semibold mb-3">
              <Sparkles className="w-3.5 h-3.5 text-[#FF5C39]" /> Choose a plan in under 3 minutes
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-[#1F0068] tracking-tight">Get a quote</h1>
            <p className="mt-3 text-[#1F0068]/70">14-day cooling off period<span className="text-[#FF5C39]">*</span> and cancellations at any time. Our plans are maintenance service agreements — not insurance products.</p>
          </div>

          <StepIndicator current={step} hideAppliances={hideAppliancesStep} />

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-3xl p-6 md:p-10 border border-[#1F0068]/5">
              {/* Step 0 — Plan Type */}
              {step === 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#1F0068] mb-2">Choose your plan type</h2>
                  <p className="text-sm text-[#1F0068]/70 mb-6">Each plan is a maintenance service agreement. Pick the one that fits your home.</p>
                  <div className="space-y-3">
                    {PLAN_TYPES.map((p) => {
                      const sel = data.planType === p.id;
                      // Preview price for selection card (min appliances = 1 for appliance plan)
                      const previewMonthly = p.id === 'appliance' ? APPLIANCE_PLAN_PRICING[0].monthly : (p.id === 'all-inclusive' ? ALL_INCLUSIVE_PLAN.monthly : null);
                      const previewAnnual = p.id === 'appliance' ? APPLIANCE_PLAN_PRICING[0].annual : (p.id === 'all-inclusive' ? ALL_INCLUSIVE_PLAN.annual : p.annualPrice);
                      return (
                        <button data-testid={`plan-type-${p.id}`} key={p.id} onClick={() => { upd({ planType: p.id, applianceIds: [], billing: p.allowsMonthly ? 'monthly' : 'annual' }); }}
                          className={`w-full p-5 md:p-6 rounded-2xl border-2 text-left transition-all ${sel ? 'border-[#1F0068] bg-[#FFEDD6]' : 'border-transparent bg-[#FFF6EB] hover:border-[#1F0068]/20'}`}>
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-xl font-bold text-[#1F0068]">{p.name}</h3>
                              <p className="text-sm text-[#1F0068]/70 mt-1">{p.tag}</p>
                              {!p.allowsMonthly && <p className="text-[11px] text-[#FF5C39] mt-1 font-semibold">To discuss monthly payment options, please call 0113 548 7557.</p>}
                            </div>
                            <div className="text-right shrink-0">
                              {previewMonthly !== null && (
                                <div className="text-[11px] text-[#1F0068]/60">From <b className="text-[#1F0068] text-base">£{previewMonthly.toFixed(2)}</b>/mo</div>
                              )}
                              <div className="text-[11px] text-[#1F0068]/60">or <b className="text-[#FF5C39] text-base">£{previewAnnual.toFixed(2)}</b>/yr</div>
                              {sel && <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#00b67a]"><Check className="w-3 h-3" /> Selected</div>}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 1 — Appliances (skipped for boiler plans) */}
              {step === 1 && !hideAppliancesStep && (
                <div>
                  <h2 className="text-2xl font-bold text-[#1F0068] mb-2">Which appliances would you like to include?</h2>
                  <p className="text-sm text-[#1F0068]/70 mb-6">This plan covers up to <b>{planType.maxAppliances}</b> appliance{planType.maxAppliances > 1 ? 's' : ''}. Selected {data.applianceIds.length}/{planType.maxAppliances}.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {applianceTypes.map((a) => {
                      const Ico = Icons[a.icon] || Icons.Package;
                      const sel = data.applianceIds.includes(a.id);
                      return (
                        <button key={a.id} type="button" data-testid={`appliance-${a.id}`} onClick={() => toggleAppliance(a.id)}
                          className={`relative p-5 rounded-2xl border-2 transition-all text-left ${sel ? 'border-[#1F0068] bg-[#FFEDD6]' : 'border-transparent bg-[#FFF6EB] hover:border-[#1F0068]/20'}`}>
                          {sel && <span className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-[#00b67a] text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" /></span>}
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${sel ? 'bg-[#1F0068] text-white' : 'bg-white text-[#1F0068]'}`}>
                            <Ico className="w-5 h-5" />
                          </div>
                          <div className="font-bold text-[#1F0068] text-sm">{a.name}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-6 space-y-4">
                    <div>
                      <Label className="text-[#1F0068] font-semibold">Primary brand (main appliance)</Label>
                      <Select value={data.brand} onValueChange={(v) => upd({ brand: v })}>
                        <SelectTrigger className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"><SelectValue placeholder="Choose a brand" /></SelectTrigger>
                        <SelectContent>
                          {applianceBrands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-[#1F0068]/60 mt-1">Brand names shown for reference only — no manufacturer partnership or endorsement is implied.</p>
                    </div>
                    <div>
                      <Label className="text-[#1F0068] font-semibold">Model number (optional)</Label>
                      <Input value={data.model} onChange={(e) => upd({ model: e.target.value })} placeholder="e.g. WAT28371GB" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 — Billing frequency */}
              {step === 2 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#1F0068] mb-2">Choose how you'd like to pay</h2>
                  <p className="text-sm text-[#1F0068]/70 mb-6">{pricing.canMonthly ? 'Pay monthly or annually — same cover either way. Cancel any time within the 14-day cooling off period.' : 'This plan is paid annually. To discuss monthly payment options, please call 0113 548 7557.'}</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {pricing.canMonthly && (
                      <button data-testid="billing-monthly" onClick={() => upd({ billing: 'monthly' })} className={`p-6 rounded-2xl border-2 text-left transition-all ${data.billing === 'monthly' ? 'border-[#1F0068] bg-[#FFEDD6]' : 'border-transparent bg-[#FFF6EB] hover:border-[#1F0068]/20'}`}>
                        <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Choose Monthly</div>
                        <div className="mt-1 text-3xl font-extrabold text-[#1F0068]">£{Number(pricing.monthly).toFixed(2)}<span className="text-sm font-medium">/month</span></div>
                        <div className="text-xs text-[#1F0068]/60 mt-1">Billed monthly by Direct Bank Transfer</div>
                        {data.billing === 'monthly' && <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#00b67a]"><Check className="w-3 h-3" /> Selected</div>}
                      </button>
                    )}
                    <button data-testid="billing-annual" onClick={() => upd({ billing: 'annual' })} className={`p-6 rounded-2xl border-2 text-left transition-all ${data.billing === 'annual' ? 'border-[#FF5C39] bg-[#FFEDD6]' : 'border-transparent bg-[#FFF6EB] hover:border-[#FF5C39]/40'}`}>
                      <div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Pay Annually</div>
                      <div className="mt-1 text-3xl font-extrabold text-[#FF5C39]">£{Number(pricing.annual).toFixed(2)}<span className="text-sm font-medium">/year</span></div>
                      <div className="text-xs text-[#1F0068]/60 mt-1">Pay once by Direct Bank Transfer</div>
                      {data.billing === 'annual' && <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#00b67a]"><Check className="w-3 h-3" /> Selected</div>}
                    </button>
                  </div>
                  <div className="mt-6 bg-[#E8DDFF] rounded-xl p-4 text-sm text-[#1F0068]">
                    <div className="font-bold flex items-center gap-2 mb-1"><Phone className="w-4 h-4 text-[#FF5C39]" /> Other payment options</div>
                    Please call our customer-service team on <a href="tel:01135487557" className="underline font-semibold">0113 548 7557</a> to discuss other available payment options.
                  </div>
                </div>
              )}

              {/* Step 3 — Your details */}
              {step === 3 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#1F0068] mb-6">Your details</h2>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#1F0068] font-semibold">Full name</Label>
                        <Input value={data.name} onChange={(e) => upd({ name: e.target.value })} placeholder="Jane Smith" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                      </div>
                      <div>
                        <Label className="text-[#1F0068] font-semibold">Email</Label>
                        <Input type="email" value={data.email} onChange={(e) => upd({ email: e.target.value })} placeholder="you@example.com" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#1F0068] font-semibold">Phone</Label>
                        <Input value={data.phone} onChange={(e) => upd({ phone: e.target.value })} placeholder="07123 456789" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                      </div>
                      <div>
                        <Label className="text-[#1F0068] font-semibold">Postcode</Label>
                        <Input value={data.postcode} onChange={(e) => upd({ postcode: e.target.value.toUpperCase() })} placeholder="SW1A 1AA" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#1F0068] font-semibold">Address</Label>
                      <Input value={data.address} onChange={(e) => upd({ address: e.target.value })} placeholder="House number and street" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                    </div>
                    <label className="flex items-start gap-3 text-sm text-[#1F0068]/70 pt-2">
                      <Checkbox checked={data.marketing} onCheckedChange={(v) => upd({ marketing: !!v })} className="mt-0.5" />
                      <span>Send me tips and offers by email. You can unsubscribe at any time.</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 4 — Payment (Direct Bank Transfer — preserved as-is) */}
              {step === 4 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#1F0068] mb-2 flex items-center gap-2"><Landmark className="w-6 h-6 text-[#FF5C39]" /> Pay by Direct Bank Transfer</h2>
                  <div className="rounded-2xl bg-gradient-to-br from-[#1F0068]/5 to-[#FF5C39]/5 border border-[#1F0068]/10 p-4 mb-5 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white border border-[#1F0068]/10 flex items-center justify-center shrink-0 mt-0.5"><Lock className="w-4 h-4 text-[#1F0068]" /></div>
                    <p className="text-[13px] text-[#1F0068]/80 leading-relaxed">
                      Pay directly from your bank using the details below. No card details are collected. For other payment options please call <a href="tel:01135487557" className="whitespace-nowrap font-bold text-[#1F0068] underline underline-offset-2 decoration-[#FF5C39]/60 hover:decoration-[#FF5C39]">0113&nbsp;548&nbsp;7557</a>.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-gradient-to-br from-[#1F0068] to-[#2A0C7A] text-white p-6 md:p-7 mb-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Send £{Number(currentPrice).toFixed(2)} ({currentBillingLabel}) to</div>
                      <button data-testid="copy-all-bank" type="button" onClick={() => copyToClipboard('All bank details', `Account name: ${COMPANY.bank.accountName}\nSort code: ${COMPANY.bank.sortCode}\nAccount number: ${COMPANY.bank.accountNumber}\nAmount: £${Number(currentPrice).toFixed(2)}\nReference: ${paymentRef}`)} className="inline-flex items-center gap-1.5 bg-[#FF5C39] hover:bg-[#e0492b] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors">
                        <Copy className="w-3 h-3" /> Copy all
                      </button>
                    </div>
                    <div className="space-y-3 text-sm">
                      <button data-testid="copy-account-name" type="button" onClick={() => copyToClipboard('Account name', COMPANY.bank.accountName)} className="w-full flex items-center justify-between gap-3 bg-white/5 hover:bg-white/10 rounded-xl px-4 py-3 transition-colors text-left group">
                        <div><div className="text-white/60 text-[10px] uppercase tracking-widest font-bold">Account name</div><div className="font-bold text-base mt-0.5">{COMPANY.bank.accountName}</div></div>
                        <Copy className="w-4 h-4 opacity-60 group-hover:opacity-100" />
                      </button>
                      <div className="grid grid-cols-2 gap-3">
                        <button data-testid="copy-sort-code" type="button" onClick={() => copyToClipboard('Sort code', COMPANY.bank.sortCode)} className="flex items-center justify-between gap-2 bg-white/5 hover:bg-white/10 rounded-xl px-4 py-3 transition-colors text-left group">
                          <div><div className="text-white/60 text-[10px] uppercase tracking-widest font-bold">Sort code</div><div className="font-bold text-lg mt-0.5 font-mono tracking-wider">{COMPANY.bank.sortCode}</div></div>
                          <Copy className="w-4 h-4 opacity-60 group-hover:opacity-100" />
                        </button>
                        <button data-testid="copy-account-number" type="button" onClick={() => copyToClipboard('Account number', COMPANY.bank.accountNumber)} className="flex items-center justify-between gap-2 bg-white/5 hover:bg-white/10 rounded-xl px-4 py-3 transition-colors text-left group">
                          <div><div className="text-white/60 text-[10px] uppercase tracking-widest font-bold">Account number</div><div className="font-bold text-lg mt-0.5 font-mono tracking-wider">{COMPANY.bank.accountNumber}</div></div>
                          <Copy className="w-4 h-4 opacity-60 group-hover:opacity-100" />
                        </button>
                      </div>
                      <button data-testid="copy-reference" type="button" onClick={() => copyToClipboard('Reference', paymentRef)} className="w-full flex items-center justify-between gap-3 bg-[#FF5C39]/20 hover:bg-[#FF5C39]/30 border border-[#FF5C39]/40 rounded-xl px-4 py-3 transition-colors text-left group">
                        <div><div className="text-[#FFEDD6] text-[10px] uppercase tracking-widest font-bold">Payment reference <span className="opacity-70">(important)</span></div><div className="font-bold text-lg mt-0.5 font-mono tracking-wider text-white">{paymentRef || '—'}</div></div>
                        <Copy className="w-4 h-4 opacity-80 group-hover:opacity-100" />
                      </button>
                      <div className="text-[11px] text-white/70 leading-relaxed pt-1">
                        Tap any row to copy. Please quote the reference <b className="text-white">{paymentRef}</b> when making your transfer so we can match your payment to your plan.
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-[#FFF6EB] border border-[#FFEDD6] p-4 mb-5 flex items-start gap-3 text-sm text-[#1F0068]">
                    <Info className="w-4 h-4 text-[#FF5C39] mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold mb-0.5">What happens next</div>
                      Once we have received your bank transfer (typically 1–2 working days), we will email your Welcome Letter, Certificate of Plan, Invoice and Terms &amp; Conditions. Your plan is activated the day payment reaches us.
                    </div>
                  </div>

                  <label className="flex items-start gap-3 text-sm text-[#1F0068] p-4 rounded-xl bg-[#FFF6EB] cursor-pointer">
                    <Checkbox data-testid="payment-acknowledged" checked={data.paymentAcknowledged} onCheckedChange={(v) => upd({ paymentAcknowledged: !!v })} className="mt-0.5" />
                    <span>I confirm I have noted the bank details above and will make my payment of <b>£{Number(currentPrice).toFixed(2)}</b> ({currentBillingLabel}) using reference <b>{paymentRef}</b> from my own online banking.</span>
                  </label>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between gap-3 pt-6 border-t border-[#1F0068]/5">
                {step > 0 ? (
                  <Button variant="ghost" onClick={back} className="rounded-full text-[#1F0068] hover:bg-[#FFF6EB] gap-1.5">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </Button>
                ) : (
                  <Link to="/"><Button variant="ghost" className="rounded-full text-[#1F0068] hover:bg-[#FFF6EB]">Cancel</Button></Link>
                )}
                {step < steps.length - 1 ? (
                  <Button data-testid="wizard-next" onClick={next} className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white h-12 px-8 gap-1.5 font-semibold">
                    Continue <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button data-testid="complete-purchase-btn" onClick={complete} className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white h-12 px-8 gap-1.5 font-semibold">
                    Confirm plan <Check className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Summary side */}
            <aside data-testid="quote-summary" className="bg-[#1F0068] text-white rounded-3xl p-6 md:p-8 h-fit sticky top-24">
              <div className="flex items-center gap-2 mb-5">
                <ShieldCheck className="w-5 h-5 text-[#FF5C39]" />
                <h3 className="font-bold text-lg">Your quote</h3>
              </div>
              <div className="space-y-3 text-sm text-white/85">
                <div className="flex items-center justify-between">
                  <span>Plan</span>
                  <span className="font-semibold text-white text-right max-w-[200px]">{planType?.name || '—'}</span>
                </div>
                {!hideAppliancesStep && (
                  <div className="flex items-center justify-between">
                    <span>Appliances</span>
                    <span className="font-semibold text-white">{selectedAppliances.length || '—'}{planType?.maxAppliances ? ` / ${planType.maxAppliances}` : ''}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span>Billing</span>
                  <span className="font-semibold text-white capitalize">{data.billing}</span>
                </div>
                <div className="h-px bg-white/10 my-2" />
                <div className="flex items-end justify-between">
                  <span>{data.billing === 'monthly' ? 'Monthly price' : 'Annual price'}</span>
                  <span data-testid="quote-price" className="text-3xl font-extrabold text-white">£{Number(currentPrice || 0).toFixed(2)}</span>
                </div>
                <div className="text-xs text-white/60">{currentBillingLabel} · Direct Bank Transfer</div>
              </div>
              <div className="mt-6 space-y-2 text-xs text-white/75" data-testid="quote-summary-legal">
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#00b67a]" /> 14 Days Cooling Off Period<span className="text-[#FF5C39]">*</span></div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#00b67a]" /> Cancel any time</div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#00b67a]" /> Maintenance service agreement</div>
                <p className="text-[10px] text-white/55 pt-1 leading-relaxed">
                  <span className="text-[#FF5C39]">*</span> Subject to conditions — see our <Link to="/legal/refund-policy" className="underline hover:text-white">Refund Policy</Link>, <Link to="/legal/cancellation-policy" className="underline hover:text-white">Cancellation Policy</Link> and <Link to="/legal/terms" className="underline hover:text-white">Terms &amp; Conditions</Link>.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GetQuote;
